import {ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../../app/store";
import {
  ZendeskTicket,
  getZendeskTicket as getTicket,
  ZendeskError,
  ZendeskUpdateUserData,
  updateZendeskUser as updateUser,
  getZendeskUserFields as getUserFields,
  ZendeskUserField
} from "../../services/ZendeskService";
import {createDefaultMatchers, SliceStatus} from "../BaseState";
import {RejectError, RejectErrorType} from "../error/Error";
import {ZendeskState} from "./ZendeskState";
import {WritableDraft} from "immer/dist/internal";
import {setZendeskTicketIdToDefaultHeader} from "../../services/BackendService";

const initialState: ZendeskState = {
  ticket: null,
  insideZendesk: null,
  ignoreTicketValue: false,
  userFields: [],
  pendings: [],
  status: SliceStatus.idle,
  error: null
};

export const getZendeskTicket = createAsyncThunk<ZendeskTicket | null, void, {rejectValue: RejectError}>(
  "zendesk/tickets/get",
  async (action: void, {rejectWithValue}) => {
    try {
      const result = await getTicket();
      return result;
    } catch (error) {
      if (error instanceof ZendeskError) return rejectWithValue({message: error.message, code: 0, type: RejectErrorType.ZENDESK});

      throw error;
    }
  },
  {
    condition: (action, {getState}: {getState: RootState}): boolean => {
      const state = getState().zendesk as ZendeskState;
      if (state.status === SliceStatus.loading || state.insideZendesk === false) {
        return false;
      }

      return true;
    }
  }
);

interface UpdateUserAction {
  readonly zendeskUserId: number;
  readonly update: ZendeskUpdateUserData;
}

export const updateZendeskUser = createAsyncThunk<void, UpdateUserAction, {rejectValue: RejectError}>(
  "zendesk/users/update",
  async (action: UpdateUserAction, {rejectWithValue}) => {
    try {
      await updateUser(action.zendeskUserId, action.update);
    } catch (error) {
      if (error instanceof ZendeskError) return rejectWithValue({message: error.message, code: 0, type: RejectErrorType.ZENDESK});

      throw error;
    }
  },
  {
    condition: (action, {getState}: {getState: RootState}): boolean => {
      const state = getState().zendesk as ZendeskState;
      if (state.status === SliceStatus.loading || state.insideZendesk === false) {
        return false;
      }

      return true;
    }
  }
);

export const getZendeskUserFields = createAsyncThunk<Array<ZendeskUserField>, void, {rejectValue: RejectError}>(
  "zendesk/users/fields/get",
  async (action: void, {rejectWithValue}) => {
    try {
      const result = await getUserFields();
      return result;
    } catch (error) {
      if (error instanceof ZendeskError) return rejectWithValue({message: error.message, code: 0, type: RejectErrorType.ZENDESK});

      throw error;
    }
  },
  {
    condition: (action, {getState}: {getState: RootState}): boolean => {
      const state = getState().zendesk as ZendeskState;
      if (state.status === SliceStatus.loading || state.insideZendesk === false) {
        return false;
      }

      return true;
    }
  }
);

const createActions = (builder: ActionReducerMapBuilder<ZendeskState>) => {
  builder
    .addCase(getZendeskTicket.fulfilled, (draft, action) => {
      draft.ticket = action.payload;
      draft.insideZendesk = true;
      setZendeskTicketIdToDefaultHeader(action.payload?.id);
    })
    .addCase(getZendeskUserFields.fulfilled, (draft, action) => {
      draft.userFields = action.payload;
    });

  createDefaultMatchers([getZendeskTicket.typePrefix, updateZendeskUser.typePrefix, getZendeskUserFields.typePrefix], builder);
};

export const zendeskSlice = createSlice({
  name: "zendesk",
  initialState,
  reducers: {
    setInsideZendesk: (draft: WritableDraft<ZendeskState>, action: PayloadAction<boolean>) => {
      draft.insideZendesk = action.payload;
    },
    setIgnoreTicketValue: (draft, action: PayloadAction<boolean>) => {
      draft.ignoreTicketValue = action.payload;
    }
  },
  extraReducers: createActions
});

export const {setInsideZendesk, setIgnoreTicketValue} = zendeskSlice.actions;
export const zendeskState = (state: RootState): ZendeskState => state.zendesk;
export default zendeskSlice.reducer;
